import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { formatStamp } from "../lib/richText";
import { GlobalSearchBar } from "../components/GlobalSearchBar";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Loader2, Rocket, Activity, Archive, FileText, Wrench, MessageSquare, Lock } from "lucide-react";

const Section = ({ icon: Icon, title, count, tone, children, testId }) => (
  <Card className="border p-4" data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <span className={`p-1.5 rounded-md ${tone}`}><Icon className="h-4 w-4" /></span>
      <h2 className="font-semibold text-base">{title}</h2>
      <span className="text-xs bg-muted px-1.5 rounded">{count}</span>
    </div>
    {count === 0 ? <p className="text-xs text-muted-foreground py-2">Nothing here.</p> : <div className="divide-y">{children}</div>}
  </Card>
);

const Row = ({ onClick, title, sub, right, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="w-full text-left py-2.5 px-1 flex items-start justify-between gap-3 hover:bg-secondary/60 rounded transition-colors">
    <div className="min-w-0">
      <div className="text-sm font-medium truncate">{title}</div>
      <div className="text-xs text-muted-foreground truncate">{sub}</div>
    </div>
    {right && <div className="shrink-0 text-right">{right}</div>}
  </button>
);

const formatDay = (d) => {
  const [y, m, day] = (d || "").slice(0, 10).split("-").map(Number);
  if (!y) return d;
  return new Date(y, m - 1, day).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};
const PROPOSAL_STATUS = { requested: "Pending", pending: "Pending", created: "Completed" };

const orderSub = (o) => [o.products?.join(", "), o.rep_name && `Rep: ${o.rep_name}`, o.created_at && `Submitted ${formatStamp(o.created_at)}`]
  .filter(Boolean).join(" · ");

const OrderList = ({ items, navigate, prefix, showStart }) => items.map((o) => (
  <Row key={o.id} testId={`${prefix}-${o.id}`} onClick={() => navigate(`/orders/${o.id}`)} title={o.client_name} sub={orderSub(o)}
    right={showStart && o.start_date ? <span className="text-xs font-medium text-amber-700">Starts {formatDay(o.start_date)}</span>
      : <Badge variant="outline" className="text-[10px] capitalize">{(o.status || "").replaceAll("_", " ")}</Badge>} />
));

export default function SearchResultsPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (q.trim().length < 2) { setData(null); return; }
    setLoading(true);
    axios.get(`${API}/search`, { params: { q } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="space-y-6" data-testid="search-results-page">
      <div>
        <h1 className="font-outfit text-3xl font-bold tracking-tight">Search</h1>
        <p className="text-muted-foreground mt-1 text-sm">{q ? <>Results for <b>&ldquo;{q}&rdquo;</b></> : "Type a client or rep name."}</p>
      </div>
      <GlobalSearchBar key={q} initial={q} autoFocus={!q} className="max-w-3xl" />
      {loading && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>}
      {!loading && data && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 space-y-5">
            <Section icon={Rocket} title="Launching" count={data.launching.length} tone="bg-amber-100 text-amber-700" testId="search-launching">
              <OrderList items={data.launching} navigate={navigate} prefix="search-launching-row" showStart />
            </Section>
            <Section icon={Activity} title="Active campaigns" count={data.active.length} tone="bg-emerald-100 text-emerald-700" testId="search-active">
              <OrderList items={data.active} navigate={navigate} prefix="search-active-row" />
            </Section>
            <Section icon={FileText} title="Proposal requests" count={data.proposals.length} tone="bg-blue-100 text-blue-700" testId="search-proposals">
              {data.proposals.map((p) => (
                <Row key={p.id} testId={`search-proposal-row-${p.id}`} onClick={() => navigate(`/proposals?open=${p.id}`)}
                  title={p.business_name} sub={[p.submitted_by_name, p.due_date && `Due ${formatStamp(p.due_date)}`].filter(Boolean).join(" · ")}
                  right={<Badge className={p.status === "requested" ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-700"}>{PROPOSAL_STATUS[p.status] || (p.status || "").replaceAll("_", " ")}</Badge>} />
              ))}
            </Section>
            <Section icon={Wrench} title="Saved prospect tools" count={data.tools.length} tone="bg-violet-100 text-violet-700" testId="search-tools">
              {data.tools.map((t) => (
                <Row key={`${t.type}-${t.id}`} testId={`search-tool-row-${t.id}`} onClick={() => navigate(t.link)}
                  title={t.name} sub={[t.label, t.by, t.created_at && formatStamp(t.created_at)].filter(Boolean).join(" · ")} />
              ))}
            </Section>
            <Section icon={Archive} title="Archived / completed" count={data.archived.length} tone="bg-zinc-100 text-zinc-600" testId="search-archived">
              <OrderList items={data.archived} navigate={navigate} prefix="search-archived-row" />
            </Section>
          </div>
          <div className="lg:col-span-2">
            <div className="lg:sticky lg:top-4">
              <Section icon={MessageSquare} title="Messages" count={data.messages.length} tone="bg-sky-100 text-sky-700" testId="search-messages">
                <div className="max-h-[75vh] overflow-y-auto divide-y">
                  {data.messages.map((m) => (
                    <Row key={`${m.kind}-${m.id}`} testId={`search-message-row-${m.id}`} onClick={() => m.link && navigate(m.link)}
                      title={<span className="inline-flex items-center gap-1.5">{m.title}{m.kind === "direct" && <Badge variant="outline" className="text-[9px] h-4">Direct</Badge>}{m.internal_only && <Lock className="h-3 w-3 text-amber-600" />}</span>}
                      sub={<><b>{m.author_name}</b>: {m.snippet || "(attachment)"}</>}
                      right={<span className="text-[10px] text-muted-foreground whitespace-nowrap">{formatStamp(m.created_at)}</span>} />
                  ))}
                </div>
              </Section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
